/*! Hyspecs — "What is PD?" help for the LensAdvisor Rx flow.
 *
 * LensAdvisor renders its prescription forms client-side inside
 * .la-prescription-modal and re-renders them on every step, so this watches
 * the DOM and adds a "What is PD?" link under each "Pupillary Distance" label
 * that sits next to a PD dropdown. The link opens the <dialog> rendered
 * server-side in sections/hys-rx-pd-help.liquid.
 *
 * Privacy: nothing here reads or sends Rx values. Opening the popup pushes a
 * bare `rx_pd_help_open` event to the dataLayer (needs a GTM trigger before
 * it shows up in GA4).
 */
class HysRxPdHelp extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    if (!this.dialog) return;
    this.label = this.dataset.linkLabel || "What is PD?";

    // Close on any [data-close] button or a tap on the backdrop.
    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog || e.target.closest("[data-close]")) {
        this.dialog.close();
      }
    });
    // Hand focus back to the link that opened it (Safari doesn't focus
    // buttons on click, so the native return can land on <body>).
    this.dialog.addEventListener("close", () => {
      if (this.opener && this.opener.isConnected) {
        this.opener.focus({ preventScroll: true });
      }
    });

    // MutationObserver already batches LA's DOM writes into one callback per
    // microtask, and the scan is a couple of querySelectorAll calls, so it
    // runs straight away (no rAF: the link must not wait on a paint).
    this.observer = new MutationObserver(() => this.addLinks());
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.addLinks();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  addLinks() {
    document.querySelectorAll(".la-prescription-modal").forEach((modal) => {
      // Only on steps that ask for PD (skips the review summary).
      if (!modal.querySelector('select[name^="la-pd"]')) return;
      modal.querySelectorAll("h6").forEach((h) => {
        if (!/pupillary distance/i.test(h.textContent)) return;
        const next = h.nextElementSibling;
        if (next && next.classList.contains("hys-pdh__link")) return;
        h.after(this.makeLink());
      });
    });
  }

  makeLink() {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "hys-pdh__link";
    btn.setAttribute("aria-haspopup", "dialog");
    btn.textContent = this.label;
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.open(btn);
    });
    return btn;
  }

  open(opener) {
    if (this.dialog.open) return;
    this.opener = opener;
    this.dialog.showModal();
    (window.dataLayer = window.dataLayer || []).push({ event: "rx_pd_help_open" });
  }
}

if (!customElements.get("hys-rx-pd-help")) {
  customElements.define("hys-rx-pd-help", HysRxPdHelp);
}
